/**
 * Web-only Stripe checkout helper.
 *
 * Uses the official @stripe/stripe-js + @stripe/react-stripe-js packages to do a
 * real card / Apple Pay / Google Pay / Link payment in the browser via Stripe's
 * unified PaymentElement. The same flow drives:
 *
 *  - Direct visits to the React web app
 *  - The embedded widget on partner sites (widget opens the React app, which
 *    renders OneClickBookingScreen)
 *
 * The native mobile app keeps using `stripePaymentService` (Stripe React Native).
 */

import type { Stripe, StripeElements } from '@stripe/stripe-js';

declare const process: { env: Record<string, string | undefined> };
declare const window: any;

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  process.env.REACT_APP_PAYMENT_API_URL ||
  'https://bookapt-payment-api.onrender.com';

const PUBLISHABLE_KEY = process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || '';

let stripePromise: Promise<Stripe | null> | null = null;

/** Lazy-load Stripe.js exactly once per page. */
export async function getStripe(): Promise<Stripe | null> {
  if (typeof window === 'undefined') return null;
  if (!PUBLISHABLE_KEY) {
    console.error('❌ webStripeCheckout: REACT_APP_STRIPE_PUBLISHABLE_KEY not set');
    return null;
  }
  if (!stripePromise) {
    const { loadStripe } = await import('@stripe/stripe-js');
    stripePromise = loadStripe(PUBLISHABLE_KEY);
  }
  return stripePromise;
}

let warmupTriggered = false;
/**
 * Fire-and-forget ping that wakes the payment API ahead of time. The Render
 * free-tier dyno can take 20-30s to cold-start, which makes Apple Pay /
 * Google Pay / Card flows look like they hang on "Preparing secure checkout…".
 * Calling this when the booking screen mounts hides the cold start entirely
 * for most users.
 *
 * Safe to call repeatedly: only the first call per page actually fires.
 */
export function warmupPaymentApi(): void {
  if (typeof window === 'undefined') return;
  if (warmupTriggered) return;
  warmupTriggered = true;
  try {
    fetch(`${API_BASE_URL}/api/health`, {
      method: 'GET',
      cache: 'no-store',
      credentials: 'omit',
      keepalive: true,
    }).catch(() => {
      /* Ignore – this is best-effort. The real intent call will surface errors. */
    });
  } catch (_) {
    /* ignore */
  }
}

export interface CreateAppointmentIntentInput {
  businessId: string;
  amount: number; // dollars
  currency?: string; // default 'usd'
  customerId?: string;
  customerName?: string;
  customerEmail?: string;
  serviceName?: string;
  metadata?: Record<string, string>;
}

export interface AppointmentIntent {
  clientSecret: string;
  paymentIntentId: string;
  mode: 'connect' | 'direct';
}

/**
 * Cap how long we'll wait for the backend to create a PaymentIntent. Render
 * cold starts top out around ~30s so 45s leaves comfortable headroom while
 * still surfacing a real failure quickly. The booking screen also calls
 * `warmupPaymentApi()` on mount which usually makes this irrelevant.
 */
const CREATE_INTENT_TIMEOUT_MS = 45_000;

/** Create a PaymentIntent on the backend for an appointment. */
export async function createAppointmentPaymentIntent(
  input: CreateAppointmentIntentInput
): Promise<AppointmentIntent> {
  // Make sure the backend is warm by the time this resolves – cheap if it
  // already happened on screen mount.
  warmupPaymentApi();

  const controller =
    typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timeoutId = controller
    ? setTimeout(() => controller.abort(), CREATE_INTENT_TIMEOUT_MS)
    : null;

  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}/api/payment/create-intent`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        businessId: input.businessId,
        amount: input.amount,
        currency: input.currency || 'usd',
        customerId: input.customerId,
        customerName: input.customerName,
        customerEmail: input.customerEmail,
        serviceName: input.serviceName,
        metadata: input.metadata,
      }),
      signal: controller ? controller.signal : undefined,
    });
  } catch (err: any) {
    if (timeoutId) clearTimeout(timeoutId);
    if (err?.name === 'AbortError') {
      throw new Error(
        'The secure checkout server took too long to respond. Please try again.'
      );
    }
    throw new Error(
      err?.message ||
        'Could not reach the payment server. Check your connection and try again.'
    );
  }
  if (timeoutId) clearTimeout(timeoutId);

  let data: any = {};
  try {
    data = await res.json();
  } catch (_) {
    /* ignore – we'll throw below */
  }

  if (!res.ok || !data?.success || !data?.clientSecret) {
    const message =
      data?.error ||
      `Failed to create payment intent (HTTP ${res.status})`;
    const err: Error & { code?: string; requiresConnect?: boolean } = new Error(message);
    if (data?.requiresConnect) err.requiresConnect = true;
    throw err;
  }

  return {
    clientSecret: data.clientSecret,
    paymentIntentId: data.paymentIntentId,
    mode: data.mode || 'direct',
  };
}

export interface ConfirmPaymentInput {
  stripe: Stripe;
  elements: StripeElements;
}

export interface ConfirmPaymentResult {
  success: boolean;
  paymentIntentId?: string;
  status?: string;
  error?: string;
  /** Whether the user cancelled (no charge attempted). */
  cancelled?: boolean;
}

/**
 * Confirm a PaymentElement-based payment without redirecting.
 * The PaymentElement may surface Apple Pay / Google Pay / Link / Card depending
 * on the browser's capabilities and the PaymentIntent's enabled methods.
 */
export async function confirmAppointmentPayment(
  input: ConfirmPaymentInput
): Promise<ConfirmPaymentResult> {
  const { stripe, elements } = input;

  const submitResult = await elements.submit();
  if (submitResult?.error) {
    return {
      success: false,
      error: submitResult.error.message || 'Payment validation failed',
      cancelled: submitResult.error.type === 'validation_error',
    };
  }

  const { error, paymentIntent } = await stripe.confirmPayment({
    elements,
    redirect: 'if_required',
  });

  if (error) {
    return {
      success: false,
      error: error.message || 'Payment failed',
      cancelled: error.code === 'payment_intent_authentication_failure',
    };
  }

  if (!paymentIntent) {
    return { success: false, error: 'No payment intent returned by Stripe' };
  }

  if (paymentIntent.status === 'succeeded' || paymentIntent.status === 'processing') {
    return {
      success: true,
      paymentIntentId: paymentIntent.id,
      status: paymentIntent.status,
    };
  }

  return {
    success: false,
    paymentIntentId: paymentIntent.id,
    status: paymentIntent.status,
    error: `Unexpected payment status: ${paymentIntent.status}`,
  };
}
